
// src/components/ClientsPage/ClientBalancePage.jsx

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { authFetch } from "../api/api";
import Modal from "../UI/Modal";
import Button from "../UI/Button";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const BALANCE_BY_CLIENT_API =
  `${BASE_URL}/api/client-balances/by-client/`;

const BALANCE_API =
  `${BASE_URL}/api/client-balances/`;


export default function ClientBalancePage() {
  const { id: clientId } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation("ClientsPage");

  // =========================================================
  // STATE
  // =========================================================

  const [balance, setBalance] = useState(null);

  const [loading, setLoading] = useState(true);

  const [isEditOpen, setIsEditOpen] = useState(false);

  const [balanceValue, setBalanceValue] = useState("");

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");


  // =========================================================
  // LOAD BALANCE
  // =========================================================

  const loadBalance = async () => {
    if (!clientId) {
      setError("Не указан ID клиента");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      /*
       * ВАЖНО:
       *
       * clientId = 25
       *
       * Этот endpoint ищет ClientBalance
       * по client_id.
       *
       * GET:
       * /api/client-balances/by-client/25/
       */

      const response = await authFetch(
        `${BALANCE_BY_CLIENT_API}${clientId}/`
      );

      if (!response.ok) {
        let message = "Не удалось загрузить баланс";

        try {
          const data = await response.json();

          if (data?.detail) {
            message = data.detail;
          }
        } catch {
          // response не JSON
        }

        throw new Error(message);
      }

      const data = await response.json();

      /*
       * Например backend вернёт:
       *
       * {
       *   "id": 1,
       *   "client": 25,
       *   "client_name": "Павел1",
       *   "balance": "765.00"
       * }
       *
       * Здесь:
       *
       * data.id     = 1  ← ID ClientBalance
       * data.client = 25 ← ID клиента
       */

      setBalance(data);

    } catch (err) {
      console.error(
        "Ошибка загрузки баланса:",
        err
      );

      setBalance(null);

      setError(
        err.message ||
        "Не удалось загрузить баланс"
      );

    } finally {
      setLoading(false);
    }
  };


  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    loadBalance();
  }, [clientId]);


  // =========================================================
  // OPEN EDIT MODAL
  // =========================================================

  const openEditModal = () => {
    if (!balance) {
      return;
    }

    setBalanceValue(
      balance.balance !== null &&
      balance.balance !== undefined
        ? String(balance.balance)
        : "0"
    );

    setError("");

    setIsEditOpen(true);
  };


  // =========================================================
  // CLOSE EDIT MODAL
  // =========================================================

  const closeEditModal = () => {
    if (saving) {
      return;
    }

    setIsEditOpen(false);

    setBalanceValue("");

    setError("");
  };


  // =========================================================
  // SAVE BALANCE
  // =========================================================

  const handleSaveBalance = async (event) => {
    event.preventDefault();

    if (saving) {
      return;
    }

    if (!balance) {
      return;
    }

    if (balanceValue.trim() === "") {
      setError("Введите баланс");
      return;
    }

    const numericBalance = Number(
      balanceValue
    );

    if (!Number.isFinite(numericBalance)) {
      setError("Введите корректную сумму");
      return;
    }

    /*
     * Используем ID ClientBalance!
     *
     * НЕ clientId.
     *
     * Например:
     *
     * clientId = 25
     * balance.id = 1
     *
     * PUT:
     * /api/client-balances/1/
     */

    const balanceId = balance.id;

    if (!balanceId) {
      setError(
        "Не найден ID записи баланса"
      );

      return;
    }

    try {
      setSaving(true);

      setError("");


      const response = await authFetch(
        `${BALANCE_API}${balanceId}/`,
        {
          method: "PUT",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            balance: numericBalance.toFixed(2),
          }),
        }
      );


      // =====================================================
      // ERROR
      // =====================================================

      if (!response.ok) {
        let message =
          "Не удалось изменить баланс";

        try {
          const data =
            await response.json();

          if (data?.detail) {
            message = data.detail;
          } else if (
            typeof data === "object" &&
            data !== null
          ) {
            message = Object.entries(data)
              .map(
                ([field, errors]) => {
                  const text =
                    Array.isArray(errors)
                      ? errors.join(", ")
                      : String(errors);

                  return `${field}: ${text}`;
                }
              )
              .join("\n");
          }
        } catch {
          // response не JSON
        }

        throw new Error(message);
      }


      // =====================================================
      // SUCCESS
      // =====================================================

      const updatedBalance =
        await response.json();

      /*
       * Backend должен вернуть:
       *
       * {
       *   "id": 1,
       *   "client": 25,
       *   "client_name": "Павел1",
       *   "balance": "5000.00",
       *   "updated_at": "..."
       * }
       */

      setBalance(
        updatedBalance
      );

      setIsEditOpen(false);

      setBalanceValue("");

    } catch (err) {
      console.error(
        "Ошибка изменения баланса:",
        err
      );

      setError(
        err.message ||
        "Не удалось изменить баланс"
      );

    } finally {
      setSaving(false);
    }
  };


  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">
          {t("loading_balance")}
        </p>
      </div>
    );
  }


  // =========================================================
  // ERROR / NOT FOUND
  // =========================================================

  if (!balance) {
    return (
      <div className="p-6 space-y-4">

        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-600">
            {error ||
              t("balance_not_found")}
          </p>
        </div>

        <Button
          onClick={() => navigate(-1)}
        >
          {t("back")}
        </Button>

      </div>
    );
  }


  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="space-y-6 p-6">

      {/* ===================================================
          HEADER
      =================================================== */}

      <div
        className="
          flex
          flex-col
          md:flex-row
          md:items-center
          md:justify-between
          gap-4
        "
      >

        <div>

          <h1 className="text-3xl font-bold">
            {t("client_balance_title")} #{clientId}
          </h1>

          <p className="text-gray-500 mt-1">
            {t("balance_details")}
          </p>

        </div>


        {/* ACTIONS */}

        <div className="flex flex-wrap gap-3">

          <Button
            variant="edit"
            onClick={openEditModal}
          >
            {t("edit")}
          </Button>


          <Button
            onClick={() => navigate(-1)}
          >
            {t("back")}
          </Button>

        </div>

      </div>


      {/* ===================================================
          BALANCE CARD
      =================================================== */}

      <div
        className="
          bg-white
          border
          rounded-2xl
          p-8
          shadow-sm
        "
      >

        <p className="text-gray-500 mb-3">
          {t("current_balance")}
        </p>


        <div
          className="
            text-5xl
            font-bold
            text-blue-600
          "
        >
          ¥{balance.balance ?? "0.00"}
        </div>


        <p className="text-sm text-gray-400 mt-3">
          CNY
        </p>


        {balance.updated_at && (
          <p className="text-xs text-gray-400 mt-4">
            Обновлено:{" "}
            {new Date(
              balance.updated_at
            ).toLocaleString("ru-RU")}
          </p>
        )}

      </div>


      {/* ===================================================
          EDIT MODAL
      =================================================== */}

      <Modal
        isOpen={isEditOpen}
        onClose={closeEditModal}
        title={t("edit_balance")}
      >

        <form
          onSubmit={handleSaveBalance}
          className="space-y-5"
        >

          {/* CURRENT */}

          <div>

            <p className="text-sm text-gray-500 mb-1">
              Текущий баланс
            </p>

            <p className="text-xl font-semibold">
              ¥{balance.balance ?? "0.00"}
            </p>

          </div>


          {/* INPUT */}

          <div>

            <label
              htmlFor="balance"
              className="
                block
                text-sm
                font-medium
                mb-2
              "
            >
              Новый баланс
            </label>

            <div className="relative">

              <input
                id="balance"
                type="number"
                step="0.01"
                min="0"
                value={balanceValue}
                onChange={(event) => {
                  setBalanceValue(
                    event.target.value
                  );

                  if (error) {
                    setError("");
                  }
                }}
                className="
                  w-full
                  rounded-xl
                  border
                  px-4
                  py-3
                  pr-16
                  outline-none
                  focus:ring-2
                  focus:ring-blue-500
                "
                placeholder="0.00"
                disabled={saving}
                autoFocus
                required
              />

              <span
                className="
                  absolute
                  right-4
                  top-1/2
                  -translate-y-1/2
                  text-gray-400
                "
              >
                ¥
              </span>

            </div>

          </div>


          {/* ERROR */}

          {error && (
            <div
              className="
                bg-red-50
                border
                border-red-200
                text-red-600
                rounded-xl
                px-4
                py-3
                text-sm
                whitespace-pre-line
              "
            >
              {error}
            </div>
          )}


          {/* BUTTONS */}

          <div
            className="
              flex
              justify-end
              gap-3
            "
          >

            <Button
              type="button"
              onClick={closeEditModal}
              disabled={saving}
            >
              {t("cancel")}
            </Button>


            <Button
              type="submit"
              variant="primary"
              disabled={saving}
            >
              {saving
                ? t("saving")
                : t("save")}
            </Button>

          </div>

        </form>

      </Modal>

    </div>
  );
}
